import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useStore } from '@/store/useStore';
import { reviewKeymapSignal } from '@/pages/review/reviewKeymapSignal';
import { Dialog } from './Dialog';
import '../system/index.css';

/**
 * Global ⌘K / Ctrl+K command palette (bd Vidi-w9i) — jump to any app
 * destination or find a word in the current language pair's library (card
 * hits deep-link into the Library drawer via ?card=<id>, the param
 * useLibraryState treats as the drawer's source of truth). North star:
 * speed to the daily loop without leaving the keyboard.
 *
 * Keymap contract (docs/DECISIONS.md): the palette is a NO-OP while a
 * review session owns the keyboard — reviewKeymapSignal.ts explains the
 * scope and the mirror of review's own suspendShortcuts pattern.
 *
 * Conventions: the redesign Dialog (native <dialog> — platform focus trap,
 * Escape, focus restore) with a visible tiny-label heading as its
 * accessible name; labels arrive as a `t` PROP, never via useTranslation
 * (manualChunks law — see AppChrome.tsx's header comment); tokens only,
 * both themes (the active option uses the lime/forest-deep AA pair, which
 * stays light-on-dark-legible in Dusk per blueprint §2). The `.rd` class on
 * the dialog itself scopes the tokens — the palette mounts in DataLoader,
 * outside any redesign Shell.
 *
 * This file lives in src/redesign/components/, so vite packs it (and the
 * system CSS it imports) into Design-shared — importing the system CSS here
 * keeps the palette styled even on the four legacy-system islands, whose
 * routes never load a redesign route chunk.
 */
type PaletteT = (key: string) => string;

interface Command {
  id: string;
  label: string;
  sublabel?: string;
  run: () => void;
}

const ROUTES: { path: string; key: string }[] = [
  { path: '/home', key: 'nav.home' },
  { path: '/create', key: 'nav.create' },
  { path: '/review', key: 'nav.review' },
  { path: '/library', key: 'nav.library' },
  { path: '/stats', key: 'nav.stats' },
  { path: '/achievements', key: 'nav.achievements' },
  { path: '/guides', key: 'nav.guides' },
  { path: '/word-pack', key: 'nav.wordPack' },
  { path: '/settings', key: 'nav.settings' },
];

export function CommandPalette({ t }: { t: PaletteT }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const navigate = useNavigate();
  const cards = useStore((s) => s.cards);
  const decks = useStore((s) => s.decks);
  const currentPair = useStore((s) => s.settings.currentLanguagePair);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.key === 'k' || e.key === 'K') && (e.metaKey || e.ctrlKey)) {
        // The review session owns its keyboard — never open over it, and
        // leave the event untouched (a true no-op).
        if (reviewKeymapSignal.ownsKeyboard) return;
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Fresh query + selection on every open; Escape-to-close and focus
  // management are the Dialog's job.
  useEffect(() => {
    if (open) {
      setQuery('');
      setActive(0);
    }
  }, [open]);

  const pairDeckIds = useMemo(
    () => new Set(decks.filter((d) => d.languagePair === currentPair).map((d) => d.id)),
    [decks, currentPair],
  );

  const commands = useMemo<Command[]>(() => {
    const q = query.trim().toLowerCase();
    const routeCmds: Command[] = ROUTES.map((r) => ({
      id: `route:${r.path}`,
      label: t(r.key),
      sublabel: r.path,
      run: () => navigate(r.path),
    })).filter((c) => !q || c.label.toLowerCase().includes(q) || c.sublabel?.includes(q));
    const cardCmds: Command[] = q
      ? cards
          .filter(
            (c) =>
              pairDeckIds.has(c.deckId) &&
              (c.targetWord.toLowerCase().includes(q) || c.nativeWord.toLowerCase().includes(q)),
          )
          .slice(0, 6)
          .map((c) => ({
            id: `card:${c.id}`,
            label: c.targetWord,
            sublabel: c.nativeWord,
            run: () => navigate(`/library?card=${c.id}`),
          }))
      : [];
    return [...routeCmds, ...cardCmds];
  }, [query, cards, pairDeckIds, t, navigate]);

  const activeIdx = commands.length ? Math.min(active, commands.length - 1) : 0;

  const run = (cmd?: Command) => {
    if (!cmd) return;
    setOpen(false);
    cmd.run();
  };

  const onInputKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, Math.max(0, commands.length - 1)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      run(commands[activeIdx]);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      labelledBy="rdh-palette-title"
      className="rd rdh-palette"
    >
      <h2 id="rdh-palette-title" className="rdh-palette-title rd-tiny-label">
        {t('cmdk.title')}
      </h2>
      <div className="rdh-palette-input-row">
        <input
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setActive(0);
          }}
          onKeyDown={onInputKey}
          placeholder={t('cmdk.placeholder')}
          className="rdh-palette-input"
          role="combobox"
          aria-expanded={true}
          aria-controls="rdh-palette-listbox"
          aria-activedescendant={commands.length ? `rdh-palette-opt-${activeIdx}` : undefined}
          aria-label={t('cmdk.placeholder')}
        />
        <kbd className="rdh-palette-kbd">Esc</kbd>
      </div>
      <ul id="rdh-palette-listbox" role="listbox" className="rdh-palette-list">
        {commands.length === 0 && <li className="rdh-palette-empty">{t('cmdk.noResults')}</li>}
        {commands.map((cmd, i) => (
          <li
            key={cmd.id}
            id={`rdh-palette-opt-${i}`}
            role="option"
            aria-selected={i === activeIdx}
          >
            <button
              type="button"
              className={i === activeIdx ? 'is-active' : ''}
              onMouseEnter={() => setActive(i)}
              onClick={() => run(cmd)}
            >
              <span>{cmd.label}</span>
              {cmd.sublabel && <span className="rdh-palette-sub">{cmd.sublabel}</span>}
            </button>
          </li>
        ))}
      </ul>
    </Dialog>
  );
}
